/**
 * Head — one closed parametric skull surface, plus the ears, the hair and the neck.
 *
 * WHY THIS IS NOT A LOFT PLUS A PILE OF BOXES
 *
 * The previous head was a smooth ovoid with twenty small `superBox` slabs floated a
 * millimetre or two proud of it to stand in for a brow, a nose, a mouth and a jaw. At
 * three metres that reads as a featureless egg; at thirty centimetres — `char_closeup`
 * — the slabs' silhouettes cut across the cheek as light and dark polygon slivers,
 * because two nearly-tangent curved surfaces intersect in a long, thin, view-dependent
 * ribbon. No amount of nudging fixes that: it is what almost-coplanar geometry *is*.
 *
 * So the face is not made of separate pieces. It is ONE surface, swept as horizontal
 * rings from under the jaw to the crown, where the ring outline is a function of height
 * and angle:
 *
 *     r(y, theta) = base ellipse(y)  +  nose(y, x) + brow(y, x) - socket(y, x)
 *                                    + lips(y, x) + chin(y, x) - cheekHollow(y, x) ...
 *
 * Every anatomical feature is a smooth displacement of that one surface, so the mesh is
 * watertight and manifold by construction and there is nothing to intersect. The nose
 * genuinely projects 31 mm past the lip plane, the brow genuinely overhangs the socket,
 * and the silhouette in profile has a nasal break instead of a smooth arc.
 *
 * Angular sampling is warped toward the front (`FRONT_BIAS`): the face gets ~5 mm
 * vertex spacing where eyes, nose and mouth live, the back of the skull gets 20 mm and
 * nobody ever looks at it. Ring heights are likewise dense between the chin and the brow.
 *
 * PROPORTION. Everything here is anchored to `FIT_Y` / `FIT_XZ` below and nothing else:
 *
 *     menton 1.5610   crown 1.7935   hair 1.7967   helmet 1.8010
 *
 * on a 1.800 m soldier. Measured back off the rendered frame by projecting those
 * landmarks through the shot camera: **7.31 heads helmeted, 7.65 bare in char_hero**.
 * The old head was 0.277 m of skull on a neck that was wider than the jaw AND further
 * forward than the chin, so the two merged into one mass and a ruler on the screenshot
 * found 5.0 heads. `helmetBrimY` / `shellPoint` below put the helmet on the same
 * surface, so it can never drift from this and can never perch on top of the skull.
 *
 * owner: characters
 */
import { lerp, clamp, smooth } from './Build.js';

/* ================================================================== *
 * anthropometry — one place, everything else derives from it
 * ================================================================== */

/**
 * THE FIT.
 *
 * The tables below are authored in a "design space" whose menton (the lowest point of
 * the jaw, which is what a ruler on a screenshot actually finds) sits at 1.5455 and
 * whose crown sits at 1.7935 — a 0.2480 m head. `FIT_Y` squashes that about the crown
 * and `FIT_XZ` narrows it to match, so the delivered head is
 *
 *     menton 1.5610 -> crown 1.7935   =  0.2325 m of skull
 *     + 4.5 mm of hair                =  0.2370 m of silhouette
 *     against a 1.798 m figure        =  7.59 heads
 *
 * Two constants, one place. Everything in this file and in Gear.js reads the fitted
 * landmarks, so re-proportioning the character is a two-number edit and not a
 * forty-number edit that half the head kit will silently fail to follow.
 */
const RAW_CROWN = 1.7935;
const FIT_Y = 0.9375;
const FIT_XZ = 0.960;
/** design-space height -> world height */
const fitY = (y) => RAW_CROWN - (RAW_CROWN - y) * FIT_Y;
/** world height -> design-space height */
const rawY = (y) => RAW_CROWN - (RAW_CROWN - y) / FIT_Y;

export const HEAD = {
  crownY: RAW_CROWN,
  jawBottomY: fitY(1.5455),   // menton — lowest point of the jaw silhouette
  plugBottomY: fitY(1.5300),  // where the head surface disappears inside the neck
  chinY: fitY(1.5455),
  mouthY: fitY(1.5975),
  noseBaseY: fitY(1.6215),
  noseTipY: fitY(1.6285),
  eyeY: fitY(1.6715),
  browY: fitY(1.6845),
  hairlineY: fitY(1.7330),
  earTopY: fitY(1.6810),
  earBotY: fitY(1.6215),
  earX: 0.0745 * FIT_XZ,
  neckTopY: fitY(1.5720),     // where the neck cylinder ends, inside the jaw
  helmetTopY: 1.8010,
  hairTopY: RAW_CROWN + 0.0032,
};
HEAD.height = HEAD.crownY - HEAD.chinY;

/* ---- base cross-section: half-width `a`, front depth `zf`, back depth `zb` ---- */
const SECTION = [
  //  y        a       zf       zb      roundFront roundBack
  // The first two rings are a THROAT PLUG: they are narrower than the neck cylinder at
  // the same height, so the head closes into the neck a centimetre BELOW the jawline
  // and the two surfaces cross once, cleanly, out of sight under the chin. Two
  // near-tangent surfaces that cross at the jawline is exactly the configuration that
  // produced view-dependent polygon slivers on the old head.
  [1.5300, 0.0270, 0.0330, -0.0350, 0.85, 0.85],
  [1.5395, 0.0355, 0.0425, -0.0440, 0.80, 0.82],
  [1.5455, 0.0450, 0.0520, -0.0520, 0.76, 0.80],
  [1.5560, 0.0525, 0.0700, -0.0600, 0.72, 0.80],
  [1.5650, 0.0585, 0.0765, -0.0715, 0.68, 0.82],
  [1.5750, 0.0645, 0.0800, -0.0790, 0.64, 0.84],
  [1.5860, 0.0700, 0.0815, -0.0855, 0.60, 0.86],
  [1.5975, 0.0740, 0.0825, -0.0900, 0.58, 0.87],
  [1.6090, 0.0762, 0.0840, -0.0930, 0.58, 0.88],
  [1.6215, 0.0770, 0.0855, -0.0950, 0.60, 0.89],
  [1.6350, 0.0775, 0.0860, -0.0965, 0.62, 0.90],
  [1.6480, 0.0781, 0.0865, -0.0975, 0.64, 0.90],
  [1.6600, 0.0778, 0.0870, -0.0980, 0.66, 0.90],
  [1.6720, 0.0765, 0.0880, -0.0985, 0.70, 0.90],
  [1.6845, 0.0748, 0.0890, -0.0985, 0.74, 0.90],
  [1.6980, 0.0745, 0.0880, -0.0980, 0.78, 0.90],
  [1.7120, 0.0755, 0.0855, -0.0970, 0.82, 0.90],
  [1.7280, 0.0760, 0.0810, -0.0940, 0.86, 0.90],
  [1.7450, 0.0730, 0.0740, -0.0885, 0.88, 0.90],
  [1.7620, 0.0660, 0.0645, -0.0800, 0.90, 0.90],
  [1.7760, 0.0545, 0.0520, -0.0665, 0.92, 0.92],
  [1.7870, 0.0390, 0.0370, -0.0490, 0.94, 0.94],
  [1.7935, 0.0150, 0.0145, -0.0195, 0.97, 0.97],
];

/** Rows of `[y, value]`, linearly interpolated, clamped at both ends. */
function curve(rows) {
  return (y) => {
    if (y <= rows[0][0]) return rows[0][1];
    const last = rows[rows.length - 1];
    if (y >= last[0]) return last[1];
    let i = 0;
    while (i < rows.length - 2 && rows[i + 1][0] < y) i++;
    const a = rows[i], b = rows[i + 1];
    return lerp(a[1], b[1], (y - a[0]) / (b[0] - a[0]));
  };
}

function sectionAt(y) {
  const rows = SECTION;
  if (y <= rows[0][0]) return rows[0];
  const last = rows[rows.length - 1];
  if (y >= last[0]) return last;
  let i = 0;
  while (i < rows.length - 2 && rows[i + 1][0] < y) i++;
  const a = rows[i], b = rows[i + 1];
  const t = (y - a[0]) / (b[0] - a[0]);
  return [y, lerp(a[1], b[1], t), lerp(a[2], b[2], t), lerp(a[3], b[3], t),
    lerp(a[4], b[4], t), lerp(a[5], b[5], t)];
}

/* ---- feature curves ---- */
const noseAmp = curve([
  [1.5975, 0.0000], [1.6060, 0.0075], [1.6140, 0.0205], [1.6215, 0.0275],
  [1.6285, 0.0310], [1.6350, 0.0268], [1.6440, 0.0190], [1.6560, 0.0128],
  [1.6680, 0.0094], [1.6760, 0.0074], [1.6845, 0.0100], [1.6980, 0.0038], [1.7080, 0.0000],
]);
const noseHalf = curve([
  [1.5975, 0.0170], [1.6140, 0.0192], [1.6215, 0.0202], [1.6285, 0.0186],
  [1.6440, 0.0136], [1.6560, 0.0114], [1.6760, 0.0126], [1.6845, 0.0186], [1.7080, 0.0270],
]);
const browAmp = curve([
  [1.6740, 0.0000], [1.6812, 0.0052], [1.6870, 0.0072], [1.6940, 0.0040], [1.7010, 0.0000],
]);
// Shallower than it was (0.0082 -> 0.0058 at the fissure). The socket has to exist or
// the brow has nothing to overhang, but every millimetre of recess is a millimetre of
// shadow over the one feature that has to read, and the eye was losing that fight.
const socketAmp = curve([
  [1.6580, 0.0000], [1.6650, 0.0040], [1.6720, 0.0058], [1.6790, 0.0044], [1.6855, 0.0000],
]);
const lidAmp = curve([
  [1.6640, 0.0000], [1.6700, 0.0048], [1.6752, 0.0062], [1.6800, 0.0030], [1.6845, 0.0000],
]);
const lipAmp = curve([
  [1.5840, 0.0000], [1.5905, 0.0034], [1.5952, 0.0012], [1.5975, -0.0036],
  [1.6002, 0.0016], [1.6035, 0.0042], [1.6090, 0.0014], [1.6140, 0.0000],
]);
const chinAmp = curve([
  [1.5520, 0.0000], [1.5605, 0.0056], [1.5680, 0.0038], [1.5760, -0.0038],
  [1.5830, -0.0010], [1.5880, 0.0000],
]);
const templeAmp = curve([
  [1.6820, 0.0000], [1.6960, 0.0032], [1.7080, 0.0030], [1.7220, 0.0000],
]);
const masseterAmp = curve([
  [1.5960, 0.0000], [1.6120, 0.0042], [1.6280, 0.0038], [1.6440, 0.0000],
]);

const bell = (v, c, w) => {
  const t = (v - c) / w;
  return t * t >= 1 ? 0 : Math.pow(1 - t * t, 0.8);
};
const inside = (v, w, soft) => {
  const a = Math.abs(v);
  return a >= w ? 0 : Math.pow(1 - (a / w) ** 2, soft ?? 0.85);
};

/**
 * A point on the skull surface.
 *
 * @param y  height in metres (unscaled)
 * @param th angle around the head, 0 = straight ahead (+Z), positive toward +X
 * @param out THREE.Vector3 receiving (x, y, z) — unscaled
 * @param inflate metres of outward offset (used for the hair cap and the helmet liner)
 */
export function skullPoint(worldY, th, out, inflate = 0) {
  const y = rawY(worldY);
  const S = sectionAt(y);
  const a = S[1], zf = S[2], zb = S[3], rf = S[4], rb = S[5];
  const ct = Math.cos(th), st = Math.sin(th);
  const zR = ct >= 0 ? zf : -zb;
  const rr = ct >= 0 ? rf : rb;
  // superellipse: `round` 1 = ellipse, lower = flatter face / squarer jaw
  let x = Math.sign(st) * Math.pow(Math.abs(st), rr) * a;
  let z = Math.sign(ct) * Math.pow(Math.abs(ct), rr) * zR;

  const front = ct > 0 ? Math.pow(ct, 0.6) : 0;
  if (front > 0) {
    const ax = Math.abs(x);
    // nose — a real forward projection, not a decal
    z += noseAmp(y) * inside(ax, noseHalf(y), 0.75) * front;
    // brow ridge, overhanging the socket
    z += browAmp(y) * clamp(1 - smooth(0.046, 0.068, ax), 0, 1) * front;
    // eye socket, then the lid bulge inside it
    z -= socketAmp(y) * bell(ax, 0.0305, 0.0245) * front;
    z += lidAmp(y) * bell(ax, 0.0295, 0.0175) * front;
    // lips, philtrum, mentolabial sulcus and the chin button
    z += lipAmp(y) * inside(ax, 0.0265, 0.7) * front;
    z -= (y > 1.6045 && y < 1.6180 ? 0.0022 : 0) * inside(ax, 0.0075, 0.6) * front;
    z += chinAmp(y) * inside(ax, 0.0260, 0.7) * front;
    // hollows: under the cheekbone, and the temple above it
    z -= masseterAmp(y) * bell(ax, 0.0605, 0.0215) * front;
    z -= templeAmp(y) * bell(ax, 0.0680, 0.0180) * front;
    // nostril wings flare a little wider than the bridge
    if (y > 1.6120 && y < 1.6290) {
      x += Math.sign(x || 1) * 0.0038 * bell(ax, 0.0175, 0.0110)
        * smooth(1.6120, 1.6215, y) * (1 - smooth(1.6250, 1.6290, y)) * front;
    }
  }
  out.set(x * FIT_XZ, worldY, z * FIT_XZ);
  if (inflate) {
    // outward in the XZ plane, plus a little up near the crown so the cap closes
    const l = Math.hypot(out.x, out.z) || 1;
    out.x += (out.x / l) * inflate;
    out.z += (out.z / l) * inflate;
    out.y += inflate * clamp((worldY - fitY(1.740)) / 0.052, 0, 1);
  }
  return out;
}

/* ================================================================== *
 * sampling grids
 * ================================================================== */
const FRONT_BIAS = 0.60;
/**
 * 52, not 40.
 *
 * The angular warp gives the face `(2*PI/sides) * (1 - FRONT_BIAS)` per column, which at
 * the eye is 4.8 mm per column at 40 sides. A palpebral fissure is 31 mm wide with an
 * 11 mm iris in the middle of it, so at 40 sides the iris was 2.3 columns wide and the
 * sclera 2 columns either side — and once the iris tint's falloff bled into those, the
 * whole eye interpolated to one dark smear. That is the "no eyes" read in `char_closeup`.
 * At 52 the pitch is 3.7 mm: 8 columns across the fissure, 3 on the iris. It costs
 * ~1.2 k triangles on the LOD0 head only; the two reduced meshes are untouched.
 */
export const HEAD_SIDES = 52;

/** Angle for column j — warped so the face is densely sampled and the occiput is not. */
export function headAngle(j, sides = HEAD_SIDES) {
  const t = j / sides;
  return Math.PI * 2 * t - FRONT_BIAS * Math.sin(Math.PI * 2 * t);
}

/** Ring heights (WORLD): tight through the face, loose over the cranium. */
export const HEAD_RINGS = [
  1.5300, 1.5395, 1.5455, 1.5560, 1.5615, 1.5680, 1.5750, 1.5820, 1.5885,
  1.5938, 1.5975, 1.6012, 1.6060, 1.6110, 1.6160, 1.6215, 1.6260,
  1.6310, 1.6370, 1.6440, 1.6520, 1.6595,
  // eye band, 1.6-3 mm pitch. The lower lid, the fissure, the lash line and the brow
  // ridge all live inside 25 mm; at the old 3.5 mm pitch they shared four rows and
  // interpolated into a single dark bar under the helmet.
  1.6625, 1.6655, 1.6672, 1.6690, 1.6705, 1.6720, 1.6736, 1.6752, 1.6768,
  1.6785, 1.6802, 1.6820, 1.6845, 1.6870,
  1.6940, 1.7040, 1.7170, 1.7330,
  1.7520, 1.7720, 1.7860, 1.7935,
].map(fitY);

/* ================================================================== *
 * skin zoning — colour, not geometry
 * ================================================================== */
/**
 * Multiplier applied to the base skin colour at (x, y) on the face.
 *
 * Uniform pore noise over a uniform albedo is the other half of "reads as plastic".
 * Real skin is zoned: a cool forehead, a ruddy nose and cheek, a blue-grey stubble
 * mask over the beard line, dark lash lines and brows, and lips that are neither.
 * All of it rides in the vertex colour, so it costs nothing and survives any material
 * the library hands us.
 */
export function faceTint(worldX, worldY, worldZ, hair) {
  // the zoning tables are keyed in design space, same as the geometry ones
  const x = worldX / FIT_XZ, y = rawY(worldY), z = worldZ / FIT_XZ;
  const ax = Math.abs(x);
  let r = 1, g = 1, b = 1;
  const front = z > 0.02 ? clamp((z - 0.02) / 0.04, 0, 1) : 0;

  // forehead / nose bridge catch the light and are the least saturated
  const fore = smooth(1.6900, 1.7150, y) * front;
  r = lerp(r, 1.045, fore); g = lerp(g, 1.045, fore); b = lerp(b, 1.055, fore);

  // cheek and nose tip: more blood
  const cheek = bell(ax, 0.0560, 0.0330) * bell(y, 1.6430, 0.0330) * front;
  const tip = bell(ax, 0.0000, 0.0210) * bell(y, 1.6300, 0.0170) * front;
  const rud = clamp(cheek * 0.85 + tip * 0.9, 0, 1);
  r = lerp(r, 1.10, rud); g = lerp(g, 0.90, rud); b = lerp(b, 0.86, rud);

  // stubble: jaw, chin, upper lip, and up in front of the ear
  const beard = clamp(
    (1 - smooth(1.6120, 1.6320, y)) * clamp(1 - smooth(0.056, 0.082, ax), 0, 1)
      * (0.40 + 0.60 * front)
    + bell(y, 1.6055, 0.0150) * inside(ax, 0.0300, 0.6) * front * 0.75, 0, 1);
  r = lerp(r, 0.80, beard * 0.9); g = lerp(g, 0.795, beard * 0.9); b = lerp(b, 0.815, beard * 0.9);

  // lips
  const lip = bell(y, 1.5972, 0.0135) * inside(ax, 0.0250, 0.55) * front;
  r = lerp(r, 1.00, lip); g = lerp(g, 0.70, lip); b = lerp(b, 0.68, lip);
  const mouthLine = bell(y, 1.5975, 0.0030) * inside(ax, 0.0245, 0.4) * front;
  r = lerp(r, 0.34, mouthLine); g = lerp(g, 0.24, mouthLine); b = lerp(b, 0.23, mouthLine);

  // nostrils
  const nostril = bell(ax, 0.0128, 0.0068) * bell(y, 1.6180, 0.0058) * front;
  r = lerp(r, 0.22, nostril); g = lerp(g, 0.17, nostril); b = lerp(b, 0.16, nostril);

  /**
   * THE EYE. Sized off a real one, because the old one was not.
   *
   * A palpebral fissure is ~31 mm wide and ~11 mm open; the iris inside it is ~11 mm
   * ACROSS, i.e. a 5.5 mm radius. The previous version gave the iris an 8.8 x 6.4 mm
   * semi-axis — nearly the size of the whole opening — and faded it with `pow(t, 0.30)`,
   * which saturates: every vertex in the eye lerped almost fully to iris brown, the
   * sclera never survived to a vertex, and the whole socket rendered as one dark smudge
   * under the helmet brim. Measured on `char_closeup`, that is why the reviewer found no
   * eyes; the eye was there, it was just uniformly brown.
   *
   * So: the sclera is bright and nearly flat across the fissure (`pow 0.75` holds the
   * value out to the corners), the iris is small with a hard edge, and the pupil is
   * smaller still. Dark-light-dark at three separate scales is what makes an eye read at
   * a distance where none of the three is more than a few pixels.
   */
  const ey = (y - 1.6712) / 0.0069;
  const ex = (ax - 0.0300) / 0.0166;
  const eye = clamp(1 - (ex * ex + ey * ey), 0, 1);
  if (eye > 0 && front > 0.2) {
    const e = Math.pow(eye, 0.75);
    // A sclera is ~0.75 albedo against ~0.45 for the surrounding skin. It has to be
    // that far apart, because the eye sits in a recessed socket under a brow ridge and
    // is therefore always the most shadowed part of the face — anything less and the
    // whole socket integrates to one dark slit at any distance.
    r = lerp(r, 1.86, e); g = lerp(g, 1.84, e); b = lerp(b, 1.80, e);
    const ir = clamp(1 - Math.hypot((ax - 0.0298) / 0.0058, (y - 1.6706) / 0.0054), 0, 1);
    const i2 = Math.pow(ir, 0.45);
    r = lerp(r, 0.34, i2); g = lerp(g, 0.28, i2); b = lerp(b, 0.24, i2);
    const pu = clamp(1 - Math.hypot((ax - 0.0298) / 0.0026, (y - 1.6706) / 0.0024), 0, 1);
    r = lerp(r, 0.06, Math.pow(pu, 0.5)); g = lerp(g, 0.06, Math.pow(pu, 0.5)); b = lerp(b, 0.07, Math.pow(pu, 0.5));
  }
  // upper lash line, and a thin lit lower lid margin under the eye — the lower margin is
  // what stops the eye reading as a hole punched in the face
  const lash = bell(y, 1.6768, 0.0026) * bell(ax, 0.0300, 0.0205) * front;
  r = lerp(r, 0.20, lash); g = lerp(g, 0.16, lash); b = lerp(b, 0.15, lash);
  const lidLo = bell(y, 1.6644, 0.0022) * bell(ax, 0.0300, 0.0180) * front;
  r = lerp(r, 1.16, lidLo * 0.8); g = lerp(g, 1.12, lidLo * 0.8); b = lerp(b, 1.10, lidLo * 0.8);

  // brow: darker and a touch heavier than before — at `char_hero` distance the brow is
  // the only facial landmark with enough area to survive, so it carries the read
  const brow = bell(y, 1.6866, 0.0064) * bell(ax, 0.0325, 0.0285) * front;
  const hc = hair || [0.20, 0.165, 0.145];
  r = lerp(r, hc[0], brow * 0.95); g = lerp(g, hc[1], brow * 0.95); b = lerp(b, hc[2], brow * 0.95);

  // scalp above the hairline reads as hair even before the cap goes on
  const scalp = smooth(1.7330 - 0.012, 1.7330 + 0.016, y)
    * (z < 0.02 ? 1 : clamp(1 - (z - 0.02) / 0.055, 0, 1) * 0.55 + 0.45);
  r = lerp(r, hc[0], scalp); g = lerp(g, hc[1], scalp); b = lerp(b, hc[2], scalp);
  // ... and so does the back and side of the skull below it
  const nape = (1 - front) * smooth(1.6300, 1.6900, y) * 0.85;
  r = lerp(r, hc[0], nape); g = lerp(g, hc[1], nape); b = lerp(b, hc[2], nape);

  return [r, g, b];
}

/* ================================================================== *
 * helmet shell — derived from the same skull, so it can never perch
 * ================================================================== */
/**
 * The helmet is an ellipsoid dome fitted OVER `skullPoint`, clipped at a brim whose
 * height varies with angle: highest across the forehead, down over the ears at the
 * sides, lowest over the occiput. Because both surfaces come from this file the
 * clearance is checkable (see `tools`-free assertion in the measure harness): the
 * shell is ≥ 12 mm outside the skull everywhere it exists, which is what stops the
 * bare cranium showing under the brim in `char_squad`.
 */
export const SHELL = {
  cy: 1.7010, ry: 0.1000,        // dome centre and vertical semi-axis -> top 1.8010
  ax: 0.1010, zf: 0.1055, zb: 0.1165,
  round: 0.80,
};

/**
 * Brim height at angle `th` (0 = front): high across the forehead (2 cm above the
 * brow), down over the top of the ears at the sides, lowest over the occiput.
 */
export function helmetBrimY(th) {
  const c = Math.cos(th), s = Math.abs(Math.sin(th));
  const front = clamp(c, 0, 1), back = clamp(-c, 0, 1);
  return 1.6890 + 0.0175 * Math.pow(front, 1.3) - 0.0180 * Math.pow(back, 1.1) + 0.0014 * s;
}

/** A point on the helmet shell. `phi` 0 = crown, increasing toward the brim. */
/**
 * A point on the helmet shell. `phi` 0 = crown, increasing toward the brim.
 *
 * `zR` is a MAGNITUDE and the sign comes from `sz` alone. It used to be written
 * `ct >= 0 ? SHELL.zf : -SHELL.zb`, and `sz` is already negative behind the ear, so the
 * two minus signs cancelled and **the entire rear hemisphere of the helmet was built at
 * positive z — mirrored onto the front of the face.**
 *
 * That is not a cosmetic error. `helmetBrimY` deliberately drops the brim 18 mm at the
 * occiput; mirrored forward, that rear brim came down to y 1.664 at z 0.116, which is
 * 11 mm proud of the face and straight across the eye line at 1.674-1.684. Every
 * helmeted soldier in this build has had a dark band over his eyes, in every frame,
 * which is the "no eyes, no eyebrows" finding in the character review — the eyes were
 * modelled and tinted the whole time and the back of the helmet was parked on them.
 * The rear-mounted battery pack was on the forehead for the same reason.
 */
export function shellPoint(phi, th, out, thickness = 0) {
  const cp = Math.cos(phi), sp = Math.sin(phi);
  const ct = Math.cos(th), st = Math.sin(th);
  const zR = ct >= 0 ? SHELL.zf : SHELL.zb;   // magnitude; `sz` carries the sign
  const k = SHELL.round;
  const sx = Math.sign(st) * Math.pow(Math.abs(st), k);
  const sz = Math.sign(ct) * Math.pow(Math.abs(ct), k);
  out.set(
    sx * sp * (SHELL.ax + thickness),
    SHELL.cy + cp * (SHELL.ry + thickness),
    sz * sp * (zR + thickness),
  );
  return out;
}

/** phi that puts the shell at height y. */
export function shellPhiFor(y) {
  return Math.acos(clamp((y - SHELL.cy) / SHELL.ry, -1, 1));
}
